import React, { useState, useRef } from 'react';
import { X, Upload, FileText, AlertCircle, CheckCircle, Download, Info } from 'lucide-react';
import toast from 'react-hot-toast';
import { courseAssignmentService } from '@/services/course-assignment.service';

interface BulkImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportSuccess: () => void;
}

interface ValidationResult {
  row_number: number;
  valid: boolean;
  errors: string[];
}

export default function BulkImportModal({ isOpen, onClose, onImportSuccess }: BulkImportModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [csvContent, setCsvContent] = useState<string>('');
  const [isValidating, setIsValidating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [validationResults, setValidationResults] = useState<ValidationResult[] | null>(null);
  const [showSample, setShowSample] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);

  const sampleCSV = `type,identifier,course_slug,deadline_days,is_mandatory,message,send_email_notification,enable_automatic_reminder,reminder_frequency_days
USER,john@example.com,python-basics,30,true,Please complete,true,true,7
USER,jane@example.com,advanced-sales,45,false,Optional course,true,false,
GROUP,engineering,leadership-101,60,false,true,true,14
GROUP,sales,sales-fundamentals,30,true,Sales training,true,true,7
ROLE,sales(sales_rep),product-training,45,true,Important!,true,false,`;

  const processFile = (selectedFile: File) => {
    const isCsv = selectedFile.name.toLowerCase().endsWith('.csv') || (selectedFile.type && selectedFile.type.includes('csv'));
    if (!isCsv) {
      toast.error('Please select a CSV file');
      return;
    }
    setFile(selectedFile);
    setValidationResults(null);
    // Read file content
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      setCsvContent(content);
    };
    reader.readAsText(selectedFile);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      processFile(selectedFile);
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounterRef.current = 0;

    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      processFile(droppedFile);
    }
  };

  const handleValidate = async () => {
    if (!csvContent) return;

    setIsValidating(true);
    setValidationResults(null);

    try {
      const result = await courseAssignmentService.validateBulkImport(csvContent, false);
      setValidationResults(result.validation_results);

      if (result.invalid_count > 0) {
        toast.error(`Found errors in ${result.invalid_count} row(s). Please check the validation results below.`);
      } else {
        toast.success(`All ${result.valid_count} row(s) are valid and ready for import`);
      }
    } catch (error: any) {
      console.error('Validation failed:', error);
      const errorMessage = error?.response?.data?.error?.error_description || error?.message || 'Failed to validate CSV file';
      toast.error(errorMessage);
    } finally {
      setIsValidating(false);
    }
  };

  const handleImport = async () => {
    if (!csvContent) return;

    const loadingToast = toast.loading('Importing assignments...');
    setIsImporting(true);

    try {
      const result = await courseAssignmentService.importBulkAssignments(csvContent, false);

      toast.dismiss(loadingToast);

      if (result.success_count > 0 && result.failed_count === 0) {
        toast.success(`Successfully imported all ${result.success_count} assignments!`);
      } else if (result.success_count > 0 && result.failed_count > 0) {
        toast.success(`Imported ${result.success_count} assignments successfully. ${result.failed_count} failed.`, {
          duration: 5000
        });
      } else if (result.failed_count > 0) {
        toast.error(`All ${result.failed_count} assignments failed to import.`);
      }

      // Close modal and refresh data
      onImportSuccess();
      handleClose();
    } catch (error: any) {
      console.error('Import failed:', error);
      toast.dismiss(loadingToast);
      const errorMessage = error?.response?.data?.description || error?.message || 'Failed to import assignments';
      toast.error(errorMessage);
    } finally {
      setIsImporting(false);
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([sampleCSV], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'course_assignment_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const resetForm = () => {
    setFile(null);
    setCsvContent('');
    setValidationResults(null);
    setIsDragging(false);
    dragCounterRef.current = 0;
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  const hasValidationErrors = validationResults && validationResults.some(r => !r.valid);
  const canImport = csvContent && !hasValidationErrors;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {/* Column layout: header and footer keep their height, only the content scrolls */}
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Bulk Import Course Assignments</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 p-6 overflow-y-auto">
          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold mb-2">How to bulk import course assignments:</p>
                <ol className="list-decimal list-inside space-y-1 ml-2">
                  <li>Download the CSV template or create your own following the format below</li>
                  <li>Fill in the assignment details (type, identifier, course_slug, etc.)</li>
                  <li>Upload the CSV file and click "Validate" to check for errors</li>
                  <li>Review validation results and fix any errors</li>
                  <li>Click "Import" to create the assignments</li>
                </ol>
              </div>
            </div>
          </div>

          {/* Sample CSV Format */}
          {showSample && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-700">Sample CSV Format</h3>
                <button
                  onClick={downloadTemplate}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  <Download className="w-4 h-4" />
                  Download Template
                </button>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 overflow-x-auto">
                <pre className="text-xs text-gray-600 whitespace-pre">{sampleCSV}</pre>
              </div>
              <div className="mt-3 text-xs text-gray-500">
                <p className="font-semibold mb-1">Column Descriptions:</p>
                <ul className="space-y-0.5">
                  <li><strong>type:</strong> USER, GROUP, or ROLE</li>
                  <li><strong>identifier:</strong> Email for USER, group_key for GROUP, group_key(role_key) for ROLE (e.g., sales(sales_rep))</li>
                  <li><strong>course_slug:</strong> The slug/URL identifier of the course to assign</li>
                  <li><strong>deadline_days:</strong> Number of days from import date (e.g., 7, 30, 90)</li>
                  <li><strong>is_mandatory:</strong> true or false (optional, defaults to false)</li>
                  <li><strong>message:</strong> Optional message for assignees</li>
                  <li><strong>send_email_notification:</strong> true or false - Send email notification to assignees (optional, defaults to true)</li>
                  <li><strong>enable_automatic_reminder:</strong> true or false - Enable automatic reminders (optional, defaults to false)</li>
                  <li><strong>reminder_frequency_days:</strong> Days between reminders (e.g., 7, 14). Required if enable_automatic_reminder is true</li>
                </ul>
              </div>
            </div>
          )}

          {/* File Upload */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Upload CSV File</h3>
            <div
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => {
                if (!file) {
                  fileInputRef.current?.click();
                }
              }}
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${!file ? 'cursor-pointer' : ''
                } ${isDragging
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-300 hover:border-gray-400'
                }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="hidden"
              />
              {!file ? (
                <div>
                  <Upload className={`w-12 h-12 mx-auto mb-3 transition-colors ${isDragging ? 'text-primary-600 scale-110' : 'text-gray-400'}`} />
                  <p className="text-sm text-gray-600 mb-2">
                    {isDragging ? 'Drop your CSV file here' : 'Drag and drop your CSV file here, or'}
                  </p>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      fileInputRef.current?.click();
                    }}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                  >
                    Choose File
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-3">
                  <FileText className="w-8 h-8 text-gray-400" />
                  <div className="text-left">
                    <p className="font-medium text-gray-900">{file.name}</p>
                    <p className="text-sm text-gray-500">
                      {(file.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      resetForm();
                    }}
                    className="ml-4 px-3 py-1 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Validation Results */}
          {validationResults && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Validation Results</h3>
              <div className={`border rounded-lg p-4 ${hasValidationErrors ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                {hasValidationErrors ? (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <AlertCircle className="w-5 h-5 text-red-600" />
                      <p className="text-sm font-semibold text-red-800">
                        Found errors in {validationResults.filter(r => !r.valid).length} row(s)
                      </p>
                    </div>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {validationResults.filter(r => !r.valid).map((result) => (
                        <div key={result.row_number} className="text-sm text-red-700">
                          <p className="font-medium">Row {result.row_number}:</p>
                          <ul className="list-disc list-inside ml-4">
                            {result.errors.map((error, idx) => (
                              <li key={idx}>{error}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <p className="text-sm text-green-800">
                      All {validationResults.length} row(s) are valid and ready for import
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="flex-shrink-0 flex flex-wrap items-center justify-between gap-3 p-6 border-t bg-gray-50">
          <button
            onClick={() => setShowSample(!showSample)}
            className="text-sm text-gray-600 hover:text-gray-800"
          >
            {showSample ? 'Hide' : 'Show'} Sample Format
          </button>
          <div className="flex gap-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleValidate}
              disabled={!csvContent || isValidating}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isValidating ? 'Validating...' : 'Validate'}
            </button>
            <button
              onClick={handleImport}
              disabled={!canImport || isImporting}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isImporting ? 'Importing...' : 'Import'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}